import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import IntuitiveTasks from './IntuitiveTasks';
import Big3History from '../Planning/Big3History';
import ActivityLibrary from '../Libraries/ActivityLibrary';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, CheckSquare, BarChart3, Calendar } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';

const TasksPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'tasks';

  // Handle tab change
  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  // Focus the quick add input when tasks tab is active
  useEffect(() => {
    if (activeTab === 'tasks') {
      // Small delay to ensure the component is rendered
      const timer = setTimeout(() => {
        const quickAddInput = document.querySelector('input[placeholder*="Add a task"]');
        if (quickAddInput) {
          quickAddInput.focus();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-stone-200">
        <div className="px-4 py-3">
          {/* Top row: Back button, Section name with color bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                style={{ minWidth: '44px', minHeight: '44px' }}
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3">
                {/* Color bar */}
                <div className="w-2 h-6 bg-azure-500 rounded-full"></div>
                <div>
                  <h1 className="text-xl font-bold text-slate-900 tracking-tight">Tasks</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom row: Tab Navigation */}
        <div className="px-0">
          <div className="flex">
            <button 
              onClick={() => handleTabChange('tasks')}
              className={`flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50 ${
                activeTab === 'tasks'
                  ? 'border-azure-500 text-slate-900 bg-azure-50'
                  : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
              }`}
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <CheckSquare className="w-4 h-4" />
                <span>Tasks</span>
              </div>
            </button>
            <button 
              onClick={() => handleTabChange('activities')}
              className={`flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50 ${
                activeTab === 'activities'
                  ? 'border-azure-500 text-slate-900 bg-azure-50'
                  : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
              }`}
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Daily Activities</span>
              </div>
            </button>
            <button
              onClick={() => handleTabChange('history')}
              className={`flex-1 px-3 sm:px-4 py-4 text-xs sm:text-sm font-medium transition-all duration-150 border-b-2 border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50 ${
                activeTab === 'history'
                  ? 'border-azure-500 text-slate-900 bg-azure-50'
                  : 'border-transparent text-stone-600 hover:text-slate-900 hover:bg-stone-50'
              }`}
              style={{ minHeight: '48px' }}
            >
              <div className="flex items-center justify-center gap-2">
                <BarChart3 className="w-4 h-4" />
                <span>History</span>
              </div>
            </button>
          </div>
        </div>
      </div>
      {/* End Header */}

      {/* Content */}
      <div className="px-2 py-2 space-y-4 pb-20">
        {activeTab === 'tasks' ? (
          /* Tasks Tab */
          <IntuitiveTasks />
        ) : activeTab === 'activities' ? (
          /* Daily Activities Tab */
          <div className="space-y-6">
            <ActivityLibrary />
          </div>
        ) : (
          /* Big 3 History Tab */
          <div className="space-y-6">
            <Big3History
              user={currentUser}
              isMobile={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksPage;